<script setup>
import { ref } from "vue";
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Head, useForm } from '@inertiajs/inertia-vue3';
import { Icon } from "@iconify/vue";
import PageHeader from "@/Components/PageHeader.vue";
import Buttons from "@/Components/Buttons.vue";
import ModalAlert from "@/Components/ModalAlert.vue";
import axios from "axios";

const props = defineProps({
    mailConfig: Object
});

const formNumeration = useForm({
    COMPLAINT_SERIES: props.mailConfig?.COMPLAINT_SERIES || 'TR-{00000000}',
});

const formSMTP = useForm({
    COMPLAINT_MAIL_HOST: props.mailConfig?.COMPLAINT_MAIL_HOST || '',
    COMPLAINT_MAIL_PORT: props.mailConfig?.COMPLAINT_MAIL_PORT || '',
    COMPLAINT_MAIL_USERNAME: props.mailConfig?.COMPLAINT_MAIL_USERNAME || '',
    COMPLAINT_MAIL_PASSWORD: props.mailConfig?.COMPLAINT_MAIL_PASSWORD || '',
    COMPLAINT_MAIL_ENCRYPTION: props.mailConfig?.COMPLAINT_MAIL_ENCRYPTION || '',
    COMPLAINT_MAIL_FROM_ADDRESS: props.mailConfig?.COMPLAINT_MAIL_FROM_ADDRESS || '',
    COMPLAINT_MAIL_FROM_NAME: props.mailConfig?.COMPLAINT_MAIL_FROM_NAME || '',
});

const showSuccessModal = ref(false);
const configSaved = ref(!!props.mailConfig?.COMPLAINT_MAIL_HOST);
const testEmail = ref('');
const isTesting = ref(false);
const testMessage = ref('');
const showTestModal = ref(false);
const testSuccess = ref(false);

const submitNumeration = () => {
    formNumeration.put(route('config-complaintbook-update'), {
        preserveScroll: true,
        onSuccess: () => {
            showSuccessModal.value = true;
        }
    });
};

const submitSMTP = () => {
    formSMTP.put(route('config-complaintbook-update'), {
        preserveScroll: true,
        onSuccess: () => {
            showSuccessModal.value = true;
            configSaved.value = true;
        }
    });
};

const sendTestEmail = async () => {
    if (!testEmail.value) return;

    isTesting.value = true;
    testMessage.value = '';

    try {
        const response = await axios.post(route('config-complaintbook-test-email'), {
            email: testEmail.value
        });

        testSuccess.value = response.data.success;
        testMessage.value = response.data.message;
        showTestModal.value = true;
    } catch (error) {
        testSuccess.value = false;
        testMessage.value = error.response?.data?.message || 'Error al enviar el correo';
        showTestModal.value = true;
    } finally {
        isTesting.value = false;
    }
};

</script>

<template>

    <Head title="Libro de Reclamaciones" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PageHeader title="Configuración de Libro de Reclamaciones">
                <template #actions>
                    <Buttons :href="route('claimlist')" color="info" size="header" icon="mdi:arrow-u-left-top-bold" label="Volver a la Lista" :hideLabelOnMobile="true" />
                </template>
            </PageHeader>
        </template>

        <div class="py-8 lg:py-10">
            <div class="mx-auto sm:px-6 lg:px-8 px-4 grid gap-4">

                <!-- Formulario de Configuración de Numeración -->
                <div class="bg-white overflow-hidden border shadow-lg rounded-md p-6">
                    <form @submit.prevent="submitNumeration" class="space-y-4">

                        <h3 class="text-lg font-bold mb-4 border-b pb-2 text-gray-800 flex items-center">
                            <Icon icon="tabler:hash" class="w-5 h-5 mr-2 text-sky-600" />
                            Configuración de Numeración
                        </h3>
                        <p class="text-sm text-gray-500 mb-6">Establece la máscara de numeración para los nuevos reclamos (ejemplo: <code>BV-{000000}</code> o <code>RQ-{000000}+50</code>).</p>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Máscara de Serie de Reclamos</label>
                                <input v-model="formNumeration.COMPLAINT_SERIES" type="text" placeholder="Ej. RQ-{000000}" class="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md shadow-sm" required />
                                <p class="text-xs text-gray-500 mt-1">Usa llaves para definir los ceros. Un <code>+N</code> opcional para sumar un offset.</p>
                            </div>
                        </div>

                        <div class="flex items-center justify-end mt-6 border-t pt-4">
                            <Buttons @click="submitNumeration" type="submit" color="info" icon="mdi:content-save" :label="formNumeration.processing ? 'Guardando...' : 'Guardar Configuración'" :disabled="formNumeration.processing" />
                        </div>
                    </form>
                </div>

                <!-- Formulario de Configuración SMTP -->
                <div class="bg-white overflow-hidden border shadow-lg rounded-md p-6">
                    <form @submit.prevent="submitSMTP" class="space-y-4">
                        <h3 class="text-lg font-bold mb-4 border-b pb-2 text-gray-800 flex items-center">
                            <Icon icon="tabler:settings" class="w-5 h-5 mr-2 text-sky-600" />
                            Configuración de Correo Electrónico (SMTP)
                        </h3>
                        <p class="text-sm text-gray-500 mb-6">Configure las credenciales SMTP que se utilizarán para enviar correos de respuesta a los clientes desde el Libro de Reclamaciones.</p>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Host SMTP</label>
                                <input v-model="formSMTP.COMPLAINT_MAIL_HOST" type="text" placeholder="Ej. smtp.gmail.com" class="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md shadow-sm" required />
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Puerto SMTP</label>
                                <input v-model="formSMTP.COMPLAINT_MAIL_PORT" type="text" placeholder="Ej. 465 o 587" class="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md shadow-sm" required />
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Usuario (Correo)</label>
                                <input v-model="formSMTP.COMPLAINT_MAIL_USERNAME" type="text" placeholder="Ej. reclamos@tuempresa.com" class="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md shadow-sm" required />
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Contraseña (App Password)</label>
                                <input v-model="formSMTP.COMPLAINT_MAIL_PASSWORD" type="password" class="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md shadow-sm" required />
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Encriptación</label>
                                <select v-model="formSMTP.COMPLAINT_MAIL_ENCRYPTION" class="w-full">
                                    <option value="ssl">SSL</option>
                                    <option value="tls">TLS</option>
                                    <option value="">Ninguno</option>
                                </select>
                            </div>
                        </div>

                        <h3 class="text-md font-bold mt-8 mb-4 border-b pb-2 text-gray-800 flex items-center">
                            Datos del Remitente
                        </h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Correo Remitente (De)</label>
                                <input v-model="formSMTP.COMPLAINT_MAIL_FROM_ADDRESS" type="email" placeholder="Ej. reclamos@tuempresa.com" class="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md shadow-sm" required />
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Nombre Remitente</label>
                                <input v-model="formSMTP.COMPLAINT_MAIL_FROM_NAME" type="text" placeholder="Ej. Atención al Cliente" class="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md shadow-sm" required />
                            </div>
                        </div>

                        <div class="flex items-center justify-end mt-6 border-t pt-4">
                            <Buttons @click="submitSMTP" type="submit" color="info" icon="mdi:content-save" :label="formSMTP.processing ? 'Guardando...' : 'Guardar Configuración'" :disabled="formSMTP.processing" />
                        </div>
                    </form>
                </div>

                <!-- Sección de Prueba de Envío -->
                <div v-if="configSaved" class="bg-white overflow-hidden border shadow-lg rounded-md p-6">
                    <h3 class="text-lg font-bold mb-4 border-b pb-2 text-gray-800 flex items-center">
                        <Icon icon="mdi:email-fast-outline" class="w-5 h-5 mr-2 text-green-600" />
                        Prueba de Envío
                    </h3>

                    <div class="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                        <h4 class="text-sm font-bold text-gray-700 mb-2">Configuración Actual Guardada:</h4>
                        <ul class="text-sm text-gray-600 grid grid-cols-1 md:grid-cols-2 gap-2">
                            <li><strong>Host:</strong> {{ props.mailConfig?.COMPLAINT_MAIL_HOST }}</li>
                            <li><strong>Puerto:</strong> {{ props.mailConfig?.COMPLAINT_MAIL_PORT }}</li>
                            <li><strong>Usuario:</strong> {{ props.mailConfig?.COMPLAINT_MAIL_USERNAME }}</li>
                            <li><strong>Remitente:</strong> {{ props.mailConfig?.COMPLAINT_MAIL_FROM_ADDRESS }} ({{ props.mailConfig?.COMPLAINT_MAIL_FROM_NAME }})</li>
                        </ul>
                    </div>

                    <p class="text-sm text-gray-500 mb-4">Ingresa un correo electrónico de prueba al que tengas acceso para verificar si la configuración SMTP funciona correctamente.</p>

                    <div class="flex items-end gap-4 max-w-md">
                        <div class="flex-grow">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Correo de destino</label>
                            <input v-model="testEmail" type="email" placeholder="tucorreo@gmail.com" class="w-full border-gray-300 focus:border-green-500 focus:ring-green-500 rounded-md shadow-sm" />
                        </div>
                        <Buttons @click="sendTestEmail" color="success" :icon="isTesting ? 'eos-icons:loading' : 'mdi:send'" label="Enviar Prueba" :disabled="isTesting || !testEmail" />
                    </div>
                </div>

            </div>
        </div>

        <ModalAlert 
            :show="showSuccessModal" 
            title="¡Éxito!" 
            message="La configuración SMTP se ha guardado correctamente en la base de datos."
            type="success"
            :showCancel="false"
            confirmText="Entendido"
            @close="showSuccessModal = false" 
            @confirm="showSuccessModal = false" 
        />
        
        <ModalAlert 
            :show="showTestModal" 
            :title="testSuccess ? '¡Envío Exitoso!' : 'Error de Envío'" 
            :message="testMessage"
            :type="testSuccess ? 'success' : 'error'"
            :showCancel="false"
            confirmText="Entendido"
            @close="showTestModal = false" 
            @confirm="showTestModal = false" 
        />
    </BreezeAuthenticatedLayout>
</template>
